/**
 * Keeps grouped membership presentation isolated from table and query state.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import { DataTableCell, DataTableRow } from '@amzn/meridian/data-table';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useTranslation } from 'src/arb-wrapper';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import type { BusinessUnitMemberDto } from 'src/store/generated/leela-api-generated';
import { GroupStatusLabelKeys, STATUS_TAG_TYPE } from 'src/utils/groupStatus';
import {
  getMembershipExpiryPresentation,
  getUserTypePresentation,
} from 'src/utils/membershipPresentation';

interface MemberTableRowProps {
  member: BusinessUnitMemberDto;
  onEdit: (member: BusinessUnitMemberDto) => void;
}

export const MemberTableRow: React.FC<MemberTableRowProps> = ({
  member,
  onEdit,
}) => {
  const { t } = useTranslation();
  const userTypePresentation = getUserTypePresentation(member.userType);

  return (
    <DataTableRow
      data-testid={`access-member-row-${member.userId}`}
      key={member.userId}
    >
      <DataTableCell data-testid={`access-member-user-${member.userId}`}>
        <Text>{member.userId}</Text>
      </DataTableCell>
      <DataTableCell>
        <Tag type={userTypePresentation.tagType}>
          {t(userTypePresentation.labelKey)}
        </Tag>
      </DataTableCell>
      <DataTableCell>
        <Column spacing="100">
          {member.groups.map((group) => {
            const expiryPresentation = getMembershipExpiryPresentation(
              group.expiresAt,
              String(t('access_management_not_available')),
            );

            return (
              <Row
                data-testid={`access-member-group-${group.membershipId}`}
                key={group.membershipId}
                spacing="200"
                wrap="down"
              >
                <ShrinkableTextValue
                  testId={`access-member-group-name-${group.membershipId}`}
                >
                  {group.groupName}
                </ShrinkableTextValue>
                {group.status !== 'ACTIVE' && (
                  <Tag
                    data-testid={`access-member-group-status-${group.membershipId}`}
                    type={STATUS_TAG_TYPE[group.status]}
                  >
                    {t(GroupStatusLabelKeys[group.status])}
                  </Tag>
                )}
                {expiryPresentation.status && (
                  <Tag
                    data-testid={`access-member-expiry-status-${group.membershipId}`}
                    type={expiryPresentation.status.tagType}
                  >
                    {t(expiryPresentation.status.labelKey)}
                  </Tag>
                )}
                <Text color="secondary" type="b200">
                  {t(
                    expiryPresentation.labelKey,
                    expiryPresentation.values,
                  )}
                </Text>
              </Row>
            );
          })}
        </Column>
      </DataTableCell>
      <DataTableCell>
        <Button
          aria-label={t('access_management_edit_user')}
          data-testid={`access-member-edit-${member.userId}`}
          type="icon"
          onClick={() => {
            onEdit(member);
          }}
        >
          <Icon tokens={editTokens} />
        </Button>
      </DataTableCell>
    </DataTableRow>
  );
};
